import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Users, Lock, MessageSquare, Search, MoreVertical, Archive, LogOut, Play, HelpCircle, Camera } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { FeedbackModal } from '@/components/feedback';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

let socket;

const Dashboard = ({ user, onSignOut }) => {
  const [activePopups, setActivePopups] = useState([]);
  const [archivedPopups, setArchivedPopups] = useState([]);
  const [newPopupName, setNewPopupName] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [latestMessages, setLatestMessages] = useState({}); // { popup_id: { username, content, timestamp } }
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [popupImage, setPopupImage] = useState(null);  // File object for popup image
  const [popupImagePreview, setPopupImagePreview] = useState('');  // Preview URL
  const [isCreating, setIsCreating] = useState(false);  // Loading state during creation
  const navigate = useNavigate();

  useEffect(() => {
    socket = io(BACKEND_URL, {
      path: '/api/socket.io',
      transports: ['websocket', 'polling']
    });

    socket.on('connect', () => {
      console.log('Socket connected');
    });

    socket.on('popup_created', (popup) => {
      setActivePopups(prev => [popup, ...prev]); // Add new popup at the beginning
    });

    socket.on('popup_updated', (popup) => {
      setActivePopups(prev => prev.map(p => p.id === popup.id ? popup : p));
    });

    socket.on('popup_removed', (data) => {
      setActivePopups(prev => prev.filter(p => p.id !== data.popup_id));
    });

    // Listen for new messages to update preview
    socket.on('dashboard_message', (data) => {
      setLatestMessages(prev => ({
        ...prev,
        [data.popup_id]: {
          username: data.username,
          content: data.content,
          timestamp: data.timestamp
        }
      }));
    });

    // Listen for user join notifications
    socket.on('user_joined_popup', (data) => {
      setLatestMessages(prev => ({
        ...prev,
        [data.popup_id]: {
          username: data.username,
          content: `joined the popup`,
          timestamp: data.timestamp,
          isJoin: true
        }
      }));
    });

    fetchActivePopups();
    fetchArchivedPopups();

    return () => {
      if (socket) socket.disconnect();
    };
  }, []);

  const fetchActivePopups = async () => {
    try {
      const response = await axios.get(`${API}/popups/active`);
      setActivePopups(response.data);
    } catch (error) {
      console.error('Error fetching popups:', error);
    }
  };

  const fetchArchivedPopups = async () => {
    try {
      const response = await axios.get(`${API}/popups/archived/${user.id}`);
      setArchivedPopups(response.data);
    } catch (error) {
      console.error('Error fetching archived popups:', error);
    }
  };

  const handleCreatePopup = async () => {
    if (!newPopupName.trim()) {
      toast.error('Please enter a popup name');
      return;
    }

    setIsCreating(true);
    const popupId = `popup_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    let imageUrl = '';

    // Upload image to Cloudinary if selected
    if (popupImage) {
      try {
        const formData = new FormData();
        formData.append('file', popupImage);
        const token = localStorage.getItem('chatAppToken');
        const res = await axios.post(`${API}/chat/upload`, formData, {
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        imageUrl = res.data.url;
      } catch (err) {
        console.error('Image upload failed:', err);
        toast.error('Image upload failed, creating popup without image');
      }
    }

    socket.emit('create_popup', {
      id: popupId,
      name: newPopupName.trim(),
      admin_id: user.id,
      admin_username: user.username,
      image: imageUrl
    });

    setNewPopupName('');
    setPopupImage(null);
    setPopupImagePreview('');
    setShowCreateDialog(false);
    setIsCreating(false);
    toast.success('Popup created successfully!');

    setTimeout(() => {
      navigate(`/chat/${popupId}`);
    }, 500);
  };

  const handleJoinPopup = (popup) => {
    // Check if user is allowed to join personal popups
    const isAllowed = popup.allowed_users?.includes(user.id) || popup.admin_id === user.id;
    if (popup.is_personal && !isAllowed) {
      toast.error('This popup is in personal mode');
      return;
    }
    navigate(`/chat/${popup.id}`);
  };

  const handleViewHistory = (popup) => {
    navigate(`/chat/${popup.id}`, { state: { isHistory: true, popup } });
  };

  const handleResumePopup = (popup) => {
    socket.emit('resume_popup', {
      popup_id: popup.id,
      admin_id: user.id
    });
    toast.success(`Resuming ${popup.name}...`);
    setTimeout(() => {
      navigate(`/chat/${popup.id}`);
    }, 500);
  };

  // Helper to format relative time (WhatsApp style)
  const getRelativeTime = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const time = new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return time.toLocaleDateString();
  };

  const filteredPopups = activePopups.filter(popup =>
    popup.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    popup.admin_username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredArchived = archivedPopups.filter(popup =>
    popup.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen flex overflow-hidden max-w-[100vw]" style={{ backgroundColor: '#2e0510' }}>
      {/* Sidebar - full width on mobile, max-w-md on desktop */}
      <div
        className="w-full md:max-w-md flex flex-col border-r overflow-hidden"
        style={{
          backgroundColor: '#260e10',
          borderColor: 'rgba(255, 200, 87, 0.1)'
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{
            backgroundColor: '#311214',
            borderColor: 'rgba(255, 200, 87, 0.1)'
          }}
        >
          <div className="flex items-center gap-3">
            <Avatar className="w-12 h-12 border-2" style={{ borderColor: '#FFC857' }}>
              <AvatarImage src={user.profile_pic} />
              <AvatarFallback style={{ background: 'linear-gradient(135deg, #FFC857 0%, #e7722eff 100%)', color: '#47030F' }} className="text-lg font-bold">
                {user.username.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h2 className="font-bold text-lg" style={{ color: '#FFC857' }}>{user.username}</h2>
              <p className="text-xs" style={{ color: '#F5E3D7', opacity: 0.7 }}>Online</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={onSignOut}
              style={{ color: '#F5E3D7' }}
              className="hover:bg-opacity-10"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  style={{ color: '#F5E3D7' }}
                  className="hover:bg-opacity-10"
                >
                  <MoreVertical className="w-5 h-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                style={{
                  backgroundColor: '#2a2a2a',
                  border: '1px solid rgba(255, 200, 87, 0.2)',
                  color: '#F5E3D7'
                }}
              >
                <DropdownMenuItem
                  onClick={() => setShowFeedbackModal(true)}
                  className="cursor-pointer hover:bg-[#47030F] focus:bg-[#47030F]"
                  style={{ color: '#F5E3D7' }}
                >
                  <HelpCircle className="w-4 h-4 mr-2" style={{ color: '#FFC857' }} />
                  Help & Feedback
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-3" style={{ backgroundColor: '#311214' }}>
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-lg"
            style={{ backgroundColor: '#2e0510' }}
          >
            <Search className="w-5 h-5" style={{ color: '#F5E3D7', opacity: 0.5 }} />
            <Input
              placeholder="Search popups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border-0 bg-transparent text-base focus-visible:ring-0 focus-visible:ring-offset-0"
              style={{ color: '#F5E3D7' }}
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="popups" className="flex-1 flex flex-col">
          <TabsList
            className="w-full rounded-none border-b justify-start gap-4 px-4"
            style={{
              backgroundColor: '#321214',
              borderColor: 'rgba(255, 200, 87, 0.1)'
            }}
          >
            <TabsTrigger
              value="popups"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#FFC857] rounded-none pb-3"
              style={{ color: '#F5E3D7' }}
              data-testid="popups-tab"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Popups
            </TabsTrigger>
            <TabsTrigger
              value="history"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-[#FFC857] rounded-none pb-3"
              style={{ color: '#F5E3D7' }}
              data-testid="history-tab"
            >
              <Archive className="w-4 h-4 mr-2" />
              Saved
            </TabsTrigger>
          </TabsList>

          {/* Active Popups */}
          <TabsContent value="popups" className="flex-1 mt-0">
            <ScrollArea className="h-[calc(100vh-240px)]">
              {filteredPopups.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6">
                  <MessageSquare className="w-16 h-16 mb-4" style={{ color: '#e64e4a', opacity: 0.3 }} />
                  <p className="text-center" style={{ color: '#F5E3D7', opacity: 0.7 }}>
                    {searchQuery ? 'No popups found' : 'No active popups yet'}
                  </p>
                  <p className="text-center text-sm mt-2" style={{ color: '#F5E3D7', opacity: 0.5 }}>
                    Create one to get started!
                  </p>
                </div>
              ) : (
                <div data-testid="active-popups-list">
                  {filteredPopups.map((popup) => (
                    <div
                      key={popup.id}
                      className="flex items-center gap-2 p-3 pr-3 transition-all duration-200 border-b"
                      style={{
                        backgroundColor: '#2e0510',
                        borderColor: 'rgba(255, 200, 87, 0.05)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e64e4a'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2e0510'}
                      data-testid={`popup-card-${popup.id}`}
                    >
                      <Avatar className="w-10 h-10 md:w-14 md:h-14 flex-shrink-0">
                        {popup.image ? (
                          <AvatarImage src={popup.image} alt={popup.name} />
                        ) : null}
                        <AvatarFallback
                          style={{
                            background: '#e74d4a',
                            color: '#FAFAFA',
                            fontSize: '18px',
                            fontWeight: 'bold'
                          }}
                        >
                          {popup.name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-base truncate" style={{ color: '#F5E3D7' }}>
                            {popup.name}
                          </h3>
                          {popup.is_personal && (
                            <Badge
                              variant="secondary"
                              style={{
                                backgroundColor: '#FFC857',
                                color: '#47030F',
                                fontSize: '10px',
                                padding: '2px 6px'
                              }}
                              data-testid="personal-badge"
                            >
                              <Lock className="w-3 h-3" />
                            </Badge>
                          )}
                        </div>

                        {/* Live message preview - WhatsApp style */}
                        {latestMessages[popup.id] ? (
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-sm truncate flex-1" style={{ color: '#F5E3D7', opacity: 0.7 }}>
                              <span style={{ color: latestMessages[popup.id].isJoin ? '#4ECDC4' : '#FFC857' }}>
                                {latestMessages[popup.id].username}
                              </span>
                              <span>: {latestMessages[popup.id].content?.slice(0, 30)}{latestMessages[popup.id].content?.length > 30 ? '...' : ''}</span>
                            </div>
                            <span className="text-xs flex-shrink-0" style={{ color: '#FFC857', opacity: 0.8 }}>
                              {getRelativeTime(latestMessages[popup.id].timestamp)}
                            </span>
                          </div>
                        ) : (
                          <div className="text-sm truncate" style={{ color: '#F5E3D7', opacity: 0.5 }}>
                            <Users className="w-3 h-3 inline mr-1" />
                            <span>Admin: {popup.admin_username} · {popup.branches.length} members</span>
                          </div>
                        )}
                      </div>

                      <Button
                        size="sm"
                        onClick={() => handleJoinPopup(popup)}
                        disabled={popup.is_personal && !popup.allowed_users?.includes(user.id) && popup.admin_id !== user.id}
                        className="font-semibold transition-all duration-300 hover:scale-105 flex-shrink-0"
                        style={{
                          background: (popup.is_personal && !popup.allowed_users?.includes(user.id) && popup.admin_id !== user.id)
                            ? 'rgba(71, 3, 15, 0.5)'
                            : '#f3b15b',
                          color: (popup.is_personal && !popup.allowed_users?.includes(user.id) && popup.admin_id !== user.id) ? '#F5E3D7' : '#47030F',
                          boxShadow: (popup.is_personal && !popup.allowed_users?.includes(user.id) && popup.admin_id !== user.id) ? 'none' : '0 2px 10px rgba(255, 200, 87, 0.3)',
                          borderRadius: '8px',
                          minWidth: '50px',
                          padding: '4px 10px'
                        }}
                        data-testid={`join-popup-btn-${popup.id}`}
                      >
                        {popup.is_personal && (popup.allowed_users?.includes(user.id) || popup.admin_id === user.id) ? 'Rejoin' : 'Join'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>

          {/* Archived/Saved Popups */}
          <TabsContent value="history" className="flex-1 mt-0">
            <ScrollArea className="h-[calc(100vh-240px)]">
              {filteredArchived.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 px-6">
                  <Archive className="w-16 h-16 mb-4" style={{ color: '#FFC857', opacity: 0.3 }} />
                  <p className="text-center" style={{ color: '#F5E3D7', opacity: 0.7 }}>
                    {searchQuery ? 'No archived popups found' : 'No archived popups yet'}
                  </p>
                  <p className="text-center text-sm mt-2" style={{ color: '#F5E3D7', opacity: 0.5 }}>
                    Close a popup with "Save & Archive" to see it here
                  </p>
                </div>
              ) : (
                <div data-testid="archived-list">
                  {filteredArchived.map((popup) => (
                    <div
                      key={popup.id}
                      className="flex items-center gap-3 p-4 transition-all duration-200 border-b"
                      style={{
                        backgroundColor: '#2a2a2a',
                        borderColor: 'rgba(255, 200, 87, 0.05)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#47030F'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#2a2a2a'}
                      data-testid={`archived-card-${popup.id}`}
                    >
                      <Avatar className="w-14 h-14 flex-shrink-0">
                        {popup.image ? (
                          <AvatarImage src={popup.image} alt={popup.name} />
                        ) : null}
                        <AvatarFallback
                          style={{
                            background: 'linear-gradient(135deg, #FFC857 0%, #e7722eff 100%)',
                            color: '#47030F',
                            fontSize: '18px',
                            fontWeight: 'bold'
                          }}
                        >
                          {popup.name.slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-base truncate" style={{ color: '#F5E3D7' }}>
                            {popup.name}
                          </h3>
                        </div>
                        <p className="text-sm truncate" style={{ color: '#F5E3D7', opacity: 0.6 }}>
                          {popup.message_count || 0} messages • {popup.participants_at_archive?.length || 0} participants
                        </p>
                        <p className="text-xs mt-1" style={{ color: '#F5E3D7', opacity: 0.4 }}>
                          Archived {new Date(popup.archived_at).toLocaleDateString()}
                        </p>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleViewHistory(popup)}
                          className="font-semibold"
                          style={{
                            backgroundColor: 'transparent',
                            border: '1px solid #FFC857',
                            color: '#FFC857'
                          }}
                        >
                          View
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleResumePopup(popup)}
                          className="font-semibold transition-all duration-300 hover:scale-105"
                          style={{
                            background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                            color: '#FAFAFA',
                            boxShadow: '0 2px 10px rgba(78, 205, 196, 0.3)'
                          }}
                        >
                          <Play className="w-4 h-4 mr-1" />
                          Resume
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {/* Create Button */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button
              className="m-4 h-14 text-base font-bold shadow-2xl transition-all duration-300 hover:scale-105"
              style={{
                background: '#e74e4a',
                color: '#47030F',
                borderRadius: '12px'
              }}
              data-testid="create-popup-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              New Popup
            </Button>
          </DialogTrigger>
          <DialogContent style={{ backgroundColor: '#260e10', border: '2px solid #e74e4a', boxShadow: 'none' }} data-testid="create-popup-dialog">
            <DialogHeader>
              <DialogTitle style={{ color: '#FFC857', fontSize: '1.5rem', textAlign: 'center' }}>Create New Popup</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              {/* Popup Image Picker */}
              <div className="flex flex-col items-center gap-3">
                <label
                  htmlFor="popup-image-input"
                  className="cursor-pointer group"
                  style={{ position: 'relative' }}
                >
                  <Avatar className="w-20 h-20 border-2 transition-all duration-300 group-hover:opacity-80" style={{ borderColor: '#FFC857' }}>
                    {popupImagePreview ? (
                      <AvatarImage src={popupImagePreview} alt="Popup" />
                    ) : null}
                    <AvatarFallback style={{
                      background: 'linear-gradient(135deg, #311214, #47030F)',
                      color: '#FFC857',
                      fontSize: '24px'
                    }}>
                      <Camera className="w-6 h-6" />
                    </AvatarFallback>
                  </Avatar>
                  <div style={{
                    position: 'absolute', bottom: -2, right: -2,
                    background: '#FFC857', borderRadius: '50%',
                    width: 22, height: 22,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <Plus style={{ width: 14, height: 14, color: '#47030F' }} />
                  </div>
                </label>
                <input
                  id="popup-image-input"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (file.size > 5 * 1024 * 1024) {
                        toast.error('Image must be under 5MB');
                        return;
                      }
                      setPopupImage(file);
                      setPopupImagePreview(URL.createObjectURL(file));
                    }
                  }}
                />
                <p className="text-xs" style={{ color: '#F5E3D7', opacity: 0.4 }}>
                  {popupImagePreview ? 'Click to change' : 'Add popup image (optional)'}
                </p>
              </div>

              <Input
                placeholder="Enter popup name"
                value={newPopupName}
                onChange={(e) => setNewPopupName(e.target.value)}
                className="h-12 text-lg border-2 focus-visible:ring-1 focus-visible:ring-[#FFC857]"
                style={{
                  borderColor: '#FFC857',
                  backgroundColor: '#311214',
                  color: '#F5E3D7'
                }}
                data-testid="popup-name-input"
              />
            </div>
            <DialogFooter>
              <Button
                onClick={handleCreatePopup}
                disabled={isCreating}
                className="w-full h-12 text-lg transition-all duration-300 hover:scale-105"
                style={{
                  background: isCreating ? '#999' : '#e74e4a',
                  color: '#47030F',
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  boxShadow: '0 6px 20px rgba(231, 78, 74, 0.4)'
                }}
                data-testid="confirm-create-popup-btn"
              >
                {isCreating ? 'Creating...' : 'Create Popup'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Main Content Area - hidden on mobile, visible on desktop */}
      <div className="hidden md:flex flex-1 items-center justify-center">
        <div className="text-center px-8">
          {/* RadChat Logo Image */}
          <img
            src="/radchat-logo.png"
            alt="RadChat"
            className="mx-auto mb-6"
            style={{ height: '102px', width: 'auto' }}
          />
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <span style={{ color: '#f9c636' }}>Welcome to </span>
            <span style={{ color: '#e74e4a' }}>RadChat</span>
          </h2>
          <p className="text-lg mb-2" style={{ color: '#f9c636', opacity: 0.8 }}>
            Select a popup from the sidebar to start chatting
          </p>
          <p className="text-sm" style={{ color: '#f2e3d6', opacity: 0.5 }}>
            Create a new popup or join an existing one
          </p>
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
      />
    </div>
  );
};

export default Dashboard;